import { camelCase } from 'lodash-es';
import React, { useCallback } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import LabeledContainer from '../../../../../../../molecules/common/LabeledContainer';
import CheckInput, { InputType } from '../../../../../../../atoms/CheckInput';
import TextInput, { TextInputType } from '../../../../../../../atoms/TextInput';

import { ValidationType } from '../../../../../../../interfaces/merchants/bookings/custom_fields';
import {
  CustomerFields,
  CustomerField,
} from '../../../../../../../interfaces/merchants/resources/books';

import ErrorMessage from '../../../../../../../molecules/merchant/resources/book/InputUserInfoErrorMessage/InputUserInfoErrorMessage';
import { InvalidCustomerInputError } from '../../../../../../../redux/actions/merchants/resources/books/api';
import css from './style.module.scss';
import classNames from 'classnames';
import TextInputDate from '../../../../../../../atoms/TextInputDate/TextInput';

interface Props {
  customerFields: CustomerFields;
  onChange: (name: string, value: string) => void;
  onChangeEmail: (email: string) => void;
  namePrefix: 'customerFields';
  values: { [name: string]: string };
  errors: InvalidCustomerInputError[];
}

const DEFINED_INPUT_TYPES: { [name: string]: TextInputType } = {
  address: TextInputType.Text,
  membership_number: TextInputType.Text,
  phone_number: TextInputType.Tel,
  zip_code: TextInputType.Tel, // number制御が辛いのでとりあえずtelにしておく
};

const FIELDS_FOR_NUMERIC_WITH_HYPHEN = ['phone_number', 'zip_code'];

const toLabel = (field: CustomerField, intl: IntlShape) => {
  const name =
    field.customLabel ||
    intl.formatMessage({ id: `books.formsLabel.${camelCase(field.name)}` });
  switch (field.validationType) {
    case ValidationType.Alphanumeric:
      return `${name} (${intl.formatMessage({
        id: 'books.inputUserInfoCustomerFields.validationLabel.alphanumeric',
      })})`;
    case ValidationType.Numeric:
      return `${name} (${intl.formatMessage({
        id: 'books.inputUserInfoCustomerFields.validationLabel.numeric',
      })})`;
    case ValidationType.Katakana:
      return `${name} (${intl.formatMessage({
        id: 'books.inputUserInfoCustomerFields.validationLabel.katakana',
      })})`;
    case ValidationType.Email:
      return `${name} (${intl.formatMessage({
        id: 'books.inputUserInfoCustomerFields.validationLabel.email',
      })})`;
    case ValidationType.Phone:
      return `${name} (${intl.formatMessage({
        id: 'books.inputUserInfoCustomerFields.validationLabel.phone',
      })})`;
    default:
      return name;
  }
};

const findValue = (
  editable: boolean,
  value?: string,
  defaultValue?: string,
) => {
  if (!editable) {
    // NOTE: editableがfalseの時にはその項目はpostしない仕様。
    //       stateに混ぜないようにするためにdefaultValueはcreateInitialStateで混ぜていないが
    //       表示やsubmittableの判定上入力状態として扱う必要があるので
    //       !editableのときにはdefaultValueを表示するようにする
    return defaultValue || '';
  }
  if (value === '') {
    return '';
  }
  return value;
};

export const submittable = (
  customerFields?: CustomerFields,
  values?: { [name: string]: string },
): boolean => {
  if (!customerFields) {
    return true;
  }
  return customerFields.fields.every((f) =>
    f.required
      ? !!findValue(f.editable, (values || {})[f.name], f.defaultValue)
      : true,
  );
};

export const createInitialState = (
  customerFields?: CustomerFields,
): Record<string, string> => {
  if (!customerFields) {
    return {};
  }
  return customerFields.fields.reduce(
    (acc, field) => {
      if (!field.editable) {
        return acc;
      }
      acc[field.name] = field.defaultValue || '';
      return acc;
    },
    {} as Record<string, string>,
  );
};

const InputUserInfoCustomerFields: React.FC<Props> = (props) => {
  const {
    namePrefix,
    values,
    customerFields,
    onChange,
    onChangeEmail,
    errors,
  } = props;
  const intl = useIntl();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        currentTarget: { name, value },
      } = e;
      const applyValue =
        FIELDS_FOR_NUMERIC_WITH_HYPHEN.indexOf(name) >= 0
          ? value.replace(/[^0-9\\-]/g, '')
          : value;
      onChange(`${namePrefix}.${name}`, applyValue);
      if (name === 'email') {
        onChangeEmail(value);
      }
    },
    [onChange, namePrefix, onChangeEmail],
  );

  const renderInput = useCallback(
    (field: CustomerField) => {
      switch (field.name) {
        case 'birthday':
          return (
            <TextInputDate
              name={field.name}
              value={findValue(
                field.editable,
                values[field.name],
                field.defaultValue,
              )
                ?.replace(/\//g, '')
                .replace(/-/g, '')}
              onChange={handleChange}
              disabled={!field.editable}
              setValue={field.defaultValue !== undefined && !field.editable}
            />
          );
        case 'gender':
          return (
            <div>
              <CheckInput
                type={InputType.Radio}
                name={field.name}
                value="female"
                onChange={handleChange}
                checked={
                  findValue(
                    field.editable,
                    values[field.name],
                    field.defaultValue,
                  ) === 'female'
                }
                disabled={!field.editable}
              >
                {intl.formatMessage({ id: `books.formsLabel.female` })}
              </CheckInput>
              <CheckInput
                type={InputType.Radio}
                name={field.name}
                value="male"
                onChange={handleChange}
                checked={
                  findValue(
                    field.editable,
                    values[field.name],
                    field.defaultValue,
                  ) === 'male'
                }
                disabled={!field.editable}
              >
                {intl.formatMessage({ id: `books.formsLabel.male` })}
              </CheckInput>
            </div>
          );
        default:
          return (
            <TextInput
              type={DEFINED_INPUT_TYPES[field.name]}
              name={field.name}
              value={findValue(
                field.editable,
                values[field.name],
                field.defaultValue,
              )}
              onChange={handleChange}
              disabled={!field.editable}
              setValue={field.defaultValue !== undefined && !field.editable}
            />
          );
      }
    },
    [handleChange, intl, values],
  );

  const labelDescription = (type: string, defaultValue?: string) => {
    if (!defaultValue && type === 'email') {
      return intl.formatMessage({ id: 'books.formsDescription.email' });
    } else {
      return undefined;
    }
  };

  return (
    <>
      {customerFields.fields.map((f) => (
        <div key={f.name}>
          <LabeledContainer
            label={toLabel(f, intl)}
            requiredLabel={f.required && f.editable}
            errorStyle={f.errors && f.errors.length !== 0}
            paddingTop={true}
            description={labelDescription(f.name, f.defaultValue)}
          >
            {renderInput(f)}
          </LabeledContainer>
          {errors
            .filter((err) => err.field === f.name)
            .map((err) => {
              if (err.type === 'already_taken') {
                return (
                  <div
                    key={err.field}
                    className={classNames(
                      css.error_msg,
                      css.ml_18px,
                      css.mt_8px,
                    )}
                  >
                    {intl.formatMessage(
                      { id: 'books.inputUserInfo.alreadyTakenError' },
                      { field: toLabel(f, intl) },
                    )}
                  </div>
                );
              } else {
                return (
                  <div
                    key={err.field}
                    className={classNames(
                      css.error_msg,
                      css.ml_18px,
                      css.mt_8px,
                    )}
                  >
                    {intl.formatMessage(
                      { id: 'books.inputUserInfo.invalidInputError' },
                      { field: toLabel(f, intl) },
                    )}
                  </div>
                );
              }
            })}
          <ErrorMessage fieldSource={f} />
        </div>
      ))}
    </>
  );
};

export default InputUserInfoCustomerFields;
